import { useEffect } from 'react';
import { Square, Clock } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTimerStore } from '@/stores/timer';
import { Button } from '@/components/ui';

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

export function TimerWidget() {
  const { activeTimer, elapsedSeconds, isLoading, fetchActiveTimer, stopTimer, tick } =
    useTimerStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    fetchActiveTimer();
  }, [fetchActiveTimer]);

  // Tick every second when timer is active, and recalculate immediately
  // when the tab regains visibility (browsers throttle setInterval in
  // background tabs, but tick() now derives elapsed time from startedAt
  // so the display corrects itself on the next tick regardless).
  useEffect(() => {
    if (!activeTimer) return;

    const interval = setInterval(() => {
      tick();
    }, 1000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        tick();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [activeTimer, tick]);

  const handleStop = async () => {
    try {
      await stopTimer();
      // Invalidate time entries so the list refreshes with the new entry
      queryClient.invalidateQueries({ queryKey: ['timeEntries'] });
    } catch (error) {
      console.error('Failed to stop timer:', error);
    }
  };

  if (!activeTimer) {
    return (
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        <Clock className="h-4 w-4" />
        <span className="text-sm">No active timer</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-3 py-2 bg-primary-50 dark:bg-primary-950 rounded-lg border border-primary-200 dark:border-primary-800">
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 bg-red-500 rounded-full animate-pulse" />
        <span className="text-lg font-mono font-semibold text-primary-700 dark:text-primary-300">
          {formatTime(elapsedSeconds)}
        </span>
      </div>
      <div className="text-sm text-primary-600 dark:text-primary-400 max-w-32 truncate">
        {activeTimer.project.name}
        {activeTimer.task && ` - ${activeTimer.task.title}`}
      </div>
      <Button
        size="sm"
        variant="danger"
        onClick={handleStop}
        isLoading={isLoading}
        leftIcon={<Square className="h-3 w-3" />}
      >
        Stop
      </Button>
    </div>
  );
}
